import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Grid, Button, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import api from "../services/api";
import { todayISO } from "../utils/date";

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "-");

export default function FreightReport() {
  const today = todayISO();
  const firstOfMonth = today.slice(0, 7) + "-01";

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(() => {
    setLoading(true);
    api.get("/freightReports/containers", { params: { fromDate, toDate } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { console.error("freight report:", e); setRows([]); })
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const totals = useMemo(() => ({
    companies:  rows.length,
    containers: rows.reduce((s, r) => s + (Number(r.ContainerCount) || 0), 0),
    bookings:   rows.reduce((s, r) => s + (Number(r.BookingCount)  || 0), 0),
  }), [rows]);

  return (
    <Box p={2}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Freight Report — Containers per Company
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField type="date" label="From" fullWidth InputLabelProps={{ shrink: true }}
              value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField type="date" label="To" fullWidth InputLabelProps={{ shrink: true }}
              value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button variant="contained" fullWidth onClick={fetchReport}>Refresh</Button>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
          <Chip variant="outlined" color="primary" label={`${totals.companies} freight compan${totals.companies === 1 ? "y" : "ies"}`} />
          <Chip variant="outlined" color="success" label={`${totals.containers} container${totals.containers === 1 ? "" : "s"}`} />
          <Chip variant="outlined" label={`${totals.bookings} booking${totals.bookings === 1 ? "" : "s"}`} />
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
          No containers in this date range.
        </Paper>
      ) : (
        rows.map((r) => (
          <CompanySection key={r.CompanyName} row={r} fromDate={fromDate} toDate={toDate} />
        ))
      )}
    </Box>
  );
}

function CompanySection({ row, fromDate, toDate }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDetails = async (_, expanded) => {
    if (!expanded || details !== null) return;
    setLoading(true);
    try {
      const r = await api.get("/freightReports/containers/detail", {
        params: { fromDate, toDate, companyName: row.CompanyName },
      });
      setDetails(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error("freight detail:", e);
      setDetails([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Accordion sx={{ mb: 1 }} onChange={loadDetails}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 1, flexWrap: "wrap", gap: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{row.CompanyName}</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip size="small" color="success"
              label={`${row.ContainerCount} container${row.ContainerCount === 1 ? "" : "s"}`}
              sx={{ fontWeight: 700 }}
            />
            <Chip size="small" variant="outlined"
              label={`${row.BookingCount} booking${row.BookingCount === 1 ? "" : "s"}`}
            />
            <Chip size="small" variant="outlined"
              label={`${fmtDate(row.FirstShipped)} → ${fmtDate(row.LastShipped)}`}
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : details === null ? null : details.length === 0 ? (
          <Typography align="center" sx={{ color: "text.secondary", py: 2 }}>
            No containers.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Container #</TableCell>
                  <TableCell>Booking #</TableCell>
                  <TableCell>Seal #</TableCell>
                  <TableCell>Shipped</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details.map((d) => (
                  <TableRow key={d.ContainerID} hover>
                    <TableCell sx={{ fontFamily: "monospace" }}>{d.ContainerNo}</TableCell>
                    <TableCell>{d.BookingNo}</TableCell>
                    <TableCell>{d.SealNo || "-"}</TableCell>
                    <TableCell>{fmtDate(d.CreatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
